/**
 * Partial Repayment Payment Schedule Modal Component
 * 部分還款付款時間表模態組件
 */
import { useState } from 'react';
import { useCustomTranslation } from '../../../hooks';
import { formatCurrency } from '../../../utils/formatters';
import { generatePaymentSchedulePDF } from '../../../utils/pdfGenerator';
import { trackPDFDownload, trackConversion } from '../../../utils/analytics';
import type { PaymentScheduleItem } from './PartialRepaymentResults';

interface PartialRepaymentScheduleProps {
  schedule: PaymentScheduleItem[] | null;
  onClose: () => void;
  newTotalPayment: number;
  newTotalInterest: number;
  newRemainingInstalments: number;
}

export default function PartialRepaymentSchedule({
  schedule,
  onClose,
  newTotalPayment,
  newTotalInterest,
  newRemainingInstalments,
}: PartialRepaymentScheduleProps) {
  const { t, i18n } = useCustomTranslation();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  if (!schedule) {
    return null;
  }

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    try {
      generatePaymentSchedulePDF({
        schedule: schedule,
        totalPayments: newRemainingInstalments,
        totalAmount: newTotalPayment,
        totalInterest: newTotalInterest,
        language: i18n.language
      });
      
      // Track PDF download
      trackPDFDownload({
        calculator_type: 'payment_schedule',
        language: i18n.language,
      });
      
      // Track conversion
      trackConversion('pdf_download_payment_schedule');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setTimeout(() => setIsGeneratingPDF(false), 1000);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('partialRepayment.paymentSchedule')}</h2>
          <button onClick={onClose} className="close-button">
            ×
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-item">
            <strong>{t('schedule.summary.totalPayments')}</strong> {newRemainingInstalments}
          </div>
          <div className="summary-item">
            <strong>{t('schedule.summary.totalAmount')}</strong> {formatCurrency(newTotalPayment)}
          </div>
          <div className="summary-item">
            <strong>{t('schedule.summary.totalInterest')}</strong> {formatCurrency(newTotalInterest)}
          </div>
        </div>

        <div className="table-container">
          <table className="amortization-table">
            <thead>
              <tr>
                <th>{t('schedule.table.number')}</th>
                <th>{t('schedule.table.payment')}</th>
                <th>{t('schedule.table.principal')}</th>
                <th>{t('schedule.table.interest')}</th>
                <th>{t('schedule.table.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((item) => (
                <tr key={item.payment_number}>
                  <td>{item.payment_number}</td>
                  <td>{formatCurrency(item.payment_amount)}</td>
                  <td>{formatCurrency(item.principal_component)}</td>
                  <td>{formatCurrency(item.interest_component)}</td>
                  <td>{formatCurrency(item.remaining_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button 
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            className="primary"
            style={{
              flex: 1,
              backgroundColor: isGeneratingPDF ? '#94a3b8' : '#10b981',
              cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
              opacity: isGeneratingPDF ? 0.7 : 1
            }}
          >
            {isGeneratingPDF ? t('schedule.downloadingPDF') : t('schedule.downloadPDF')}
          </button>
          <button onClick={onClose} className="secondary" style={{ flex: 1 }}>
            {t('schedule.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
